import { useEffect } from 'react';

import { useLocalStorage } from '@/hooks/useLocalStorage';
import {
  applyProductConfigurationFilters,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { WatchHit } from '@/integrations/algolia/types';
import {
  CAROUSEL_FETCH_LIMIT,
  LOCAL_STORAGE_KEY,
  NEXT_PUBLIC_DEFAULT_APP_LOCALE,
} from '@/lib/const';

export const useRecentlyViewedProducts = (sku: string | undefined | null) => {
  const [recentlyViewedProductRef, setRecentlyViewedProductRef] =
    useLocalStorage(LOCAL_STORAGE_KEY.RecentlyViewedProduct, [] as string[], {
      sync: true,
    });
  useEffect(() => {
    if (sku) {
      // sku first so the Set keeps it at the front on revisit; add() left it put.
      const newArr = Array.from(
        new Set([sku, ...recentlyViewedProductRef]),
      ).slice(0, CAROUSEL_FETCH_LIMIT);

      setRecentlyViewedProductRef(newArr);
    }
  }, [sku]);
  return recentlyViewedProductRef;
};

export const getWatchHitsByAlgoliaSsrSearchClient = async ({
  draftMode,
  locale,
  sku,
}: {
  draftMode?: boolean;
  locale?: string;
  sku: string;
}) => {
  const indexName = getIndexName(
    'PRODUCT',
    locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  );
  const algoliaFilters = applyProductConfigurationFilters(
    ProductConfigurationFilterType.PDP,
    'Watch',
    draftMode,
    `objectID:${sku}`,
  );

  const algoliaSsrSearchClient = createSearchClient({
    isPreview: draftMode,
    isSsr: true,
  });
  const index = algoliaSsrSearchClient.initIndex(indexName);
  const result = await index.search<WatchHit>(sku, {
    filters: algoliaFilters,
    analyticsTags: ['WATCH', 'WEB', 'PDP'],
    clickAnalytics: true,
    distinct: true,
  });
  return result;
};
